import { Box } from "@mui/material";
import { addDays, addMonths, format, getDaysInMonth } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import { SchedulingPeriod } from "../settingsApi";
import { SchedulingPeriodView } from "./SchedulingPeriodView";
import { RoundedButton } from "../../../common/components/styled/styled";

interface Props {
  type: string;
  selectedYear: string;
  previousScheduleEndDate?: string;
  onSave: (schedulingPeriod: SchedulingPeriod) => void;
}

export const AddSchedulingPeriod = ({
  type,
  selectedYear,
  previousScheduleEndDate,
  onSave,
}: Props) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const tempDate = new Date().getTime();
  const [newSchedulingPeriod, setNewSchedulingPeriod] =
    useState<SchedulingPeriod>({
      start: "",
      end: "",
      entriesApprovedByCustomer: tempDate,
      entriesApprovedBySystem: tempDate,
      invoicesSentWave1: tempDate,
      invoicesSentWave2: tempDate,
      salaryDate: "",
      valueDate: "",
    });

  const save = (schedulingPeriod: SchedulingPeriod) => {
    setIsModalOpen(false);
    onSave(schedulingPeriod);
  };

  const setScheduleDates = (startDate: Date, endDate: Date) => {
    const dateFormat = "yyyy-MM-dd";
    const customer = addDays(endDate, 2);
    const system = addDays(customer, 1);
    const invoices = addDays(system, 1);
    const invoices1Wave = addDays(invoices, 1);
    const invoices2Wave = addDays(invoices1Wave, 1);
    const valueDate = format(endDate, dateFormat);

    let salaryDateString: string = "";

    if (type === "schc-1m") {
      // ONCE IN MONTH
      // Salary date is 12th day of the next month.
      salaryDateString = format(
        addMonths(
          new Date(`${endDate.getFullYear()}-${endDate.getMonth() + 1}-12`),
          1,
        ),
        dateFormat,
      );
    } else if (type === "schc-2m") {
      // TWICE IN MONTH
      // Salary date is 27th of current month if period ends on 15th.
      // Otherwise it's 12th of next month.
      let salaryDate =
        endDate.getDate() === 15
          ? new Date(`${endDate.getFullYear()}-${endDate.getMonth() + 1}-27`)
          : addMonths(
              new Date(`${endDate.getFullYear()}-${endDate.getMonth() + 1}-12`),
              1,
            );
      salaryDateString = format(salaryDate, dateFormat);
    } else if (type === "schc-2w-odd" || type === "schc-2w-even") {
      // TWO WEEKS ODD-EVEN and TWO WEEKS EVEN-ODD
      // Salary date is friday of the next week (even or odd).
      salaryDateString = format(addDays(new Date(endDate), 5), dateFormat);
    }

    setNewSchedulingPeriod({
      ...newSchedulingPeriod,
      start: format(startDate, dateFormat),
      end: format(endDate, dateFormat),
      entriesApprovedByCustomer: customer.getTime(),
      entriesApprovedBySystem: system.getTime(),
      invoicesSentWave1: invoices1Wave.getTime(),
      invoicesSentWave2: invoices2Wave.getTime(),
      valueDate,
      salaryDate: salaryDateString,
    });
  };

  const calculateDateRange = () => {
    const previousEndDate = !!previousScheduleEndDate
      ? new Date(previousScheduleEndDate)
      : undefined;

    // Start date is the next day from the end of the previous period.
    // If no previous period is found, SUGGESTED start date is 1st day of the year.

    const firstDayOfYear = new Date(`${selectedYear}-01-01`);
    const startDate = !!previousEndDate
      ? addDays(new Date(previousEndDate), 1)
      : firstDayOfYear;

    let endDate: Date = new Date();

    if (type === "schc-1m") {
      // ONCE IN MONTH
      // End date is always the last day of the month.
      const lastDay = getDaysInMonth(startDate);
      endDate = new Date(
        `${startDate.getFullYear()}-${startDate.getMonth() + 1}-${lastDay}`,
      );
    } else if (type === "schc-2m") {
      // TWICE IN MONTH
      // If start date is 1st day, end date is 15th,
      // otherwise it's the last day of the month.
      endDate =
        startDate.getDate() === 1
          ? new Date(
              `${startDate.getFullYear()}-${startDate.getMonth() + 1}-15`,
            )
          : new Date(
              `${startDate.getFullYear()}-${
                startDate.getMonth() + 1
              }-${getDaysInMonth(startDate)}`,
            );
    } else if (type === "schc-2w-odd" || type === "schc-2w-even") {
      // TWO WEEKS ODD and TWO WEEKS EVEN
      // End date is 13 days from start date (landing on an even or odd Sunday)
      endDate = addDays(new Date(startDate), 13);
    }

    setScheduleDates(startDate, endDate);
  };

  return (
    <Box>
      <RoundedButton
        disabled={!type || !selectedYear}
        onClick={() => {
          setIsModalOpen(true);
          calculateDateRange();
        }}
      >
        {t("add")}
      </RoundedButton>
      <DialogComponent
        autoWidth
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <SchedulingPeriodView
          type={type}
          isNewSchedulingPeriod={true}
          row={newSchedulingPeriod}
          onClose={() => setIsModalOpen(false)}
          onSave={save}
        />
      </DialogComponent>
    </Box>
  );
};
